/**
 * LessonsRail — horizontal scroll rail of Lesson cards, sits at the
 * top of the Insights pages (PD + Team).
 *
 * "See all" toggles between rail mode (3-4 visible, horizontal scroll)
 * and grid mode (every lesson laid out in a vertical grid). Designed
 * to scale from 1 lesson up to ~50 without needing a dedicated route.
 *
 * Cards click out via onOpen(lessonKey). The parent is responsible for
 * resolving the key against the module map and opening the reader.
 */

import React, { useState } from "react";
import { ChevronRight, BookOpen, Clock, ChevronDown } from "lucide-react";
import { GOLD } from "../constants/theme";

const hf = "'Bebas Neue',sans-serif";

export function LessonsRail({ lessons, C, dark, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  if (!lessons || lessons.length === 0) return null;

  return (
    <div style={{ marginBottom: 28 }}>
      {/* Section header with see-all toggle */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <BookOpen size={14} color={GOLD} style={{ position: "relative", top: 1 }}/>
          <div style={{ fontFamily: hf, fontSize: 18, color: C.text, letterSpacing: 2 }}>LESSONS</div>
          <div style={{ fontSize: 11, color: C.textDim }}>
            {lessons.length} short read{lessons.length === 1 ? "" : "s"} on a single concept
          </div>
        </div>
        {lessons.length > 4 && (
          <button onClick={() => setExpanded(e => !e)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              background: "transparent", border: "none",
              color: GOLD, fontSize: 12, fontWeight: 700,
              cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
              padding: 0,
            }}>
            {expanded ? "Show less" : "See all"}
            {expanded
              ? <ChevronDown size={13} style={{ transform: "rotate(180deg)" }}/>
              : <ChevronRight size={13}/>}
          </button>
        )}
      </div>

      {/* Local styles — rail scroll snap + custom scrollbar. Scoped so
          they can't leak into other surfaces that use horizontal scroll. */}
      <style>{`
        .bam-lessons-rail {
          scroll-snap-type: x mandatory;
          scrollbar-width: thin;
          scrollbar-color: ${GOLD}33 transparent;
        }
        .bam-lessons-rail::-webkit-scrollbar { height: 6px; }
        .bam-lessons-rail::-webkit-scrollbar-thumb { background: ${GOLD}22; border-radius: 3px; }
        .bam-lessons-rail > * { scroll-snap-align: start; flex: 0 0 auto; }
        .bam-lesson-card { transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
        .bam-lesson-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(0,0,0,0.32), 0 0 0 1px ${GOLD}55;
          border-color: ${GOLD}55;
        }
      `}</style>

      {/* Rail or grid */}
      {expanded ? (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 14,
        }}>
          {lessons.map(l => (
            <LessonCard key={l.key} lesson={l} C={C} dark={dark} onOpen={onOpen}/>
          ))}
        </div>
      ) : (
        <div className="bam-lessons-rail" style={{
          display: "flex", gap: 14,
          overflowX: "auto", overflowY: "visible",
          paddingBottom: 8, // room for the hover lift + shadow without clipping
          marginLeft: -4, paddingLeft: 4, // align edge with the section header
        }}>
          {lessons.map(l => (
            <div key={l.key} style={{ width: 280 }}>
              <LessonCard lesson={l} C={C} dark={dark} onOpen={onOpen}/>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LessonCard({ lesson, C, dark, onOpen }) {
  return (
    <button
      onClick={() => onOpen?.(lesson.key)}
      className="bam-lesson-card"
      style={{
        background: C.cardGradient || C.bgCard || "rgba(255,255,255,0.03)",
        border: `1px solid ${C.cardBorder || C.border}`,
        borderRadius: 12,
        padding: 0,
        overflow: "hidden",
        cursor: "pointer",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        fontFamily: "'DM Sans', sans-serif",
        color: "inherit",
      }}>
      {/* Cover — uses the lesson's cover image if set, otherwise a
          gold-tinted gradient with the title burned in. Either way the
          card reads as a Lesson, not a masterclass card. */}
      <div style={{
        aspectRatio: "16/9",
        position: "relative",
        background: lesson.cover
          ? `url(${lesson.cover}) center/cover`
          : `linear-gradient(135deg, #1A1A1A 0%, #0C0C0C 60%, ${GOLD}18 100%)`,
        borderBottom: `1px solid ${C.cardBorder || C.border}`,
        overflow: "hidden",
      }}>
        {/* Subtle grid + gold accent bar on the gradient version */}
        {!lesson.cover && (
          <>
            <div aria-hidden="true" style={{
              position: "absolute", inset: 0,
              background: `repeating-linear-gradient(0deg, transparent 0 23px, ${GOLD}06 23px 24px), repeating-linear-gradient(90deg, transparent 0 23px, ${GOLD}06 23px 24px)`,
              pointerEvents: "none",
            }}/>
            <div style={{ position: "absolute", top: 14, left: 14, width: 28, height: 2, background: GOLD }}/>
            <div style={{
              position: "absolute", inset: 0,
              padding: "32px 16px 16px",
              display: "flex", flexDirection: "column", justifyContent: "flex-end",
            }}>
              <div style={{
                fontFamily: hf, fontSize: 22, color: "#F5F5F5",
                lineHeight: 1.05, letterSpacing: 1,
                textShadow: "0 2px 12px rgba(0,0,0,0.5)",
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
              }}>
                {lesson.title.toUpperCase()}
              </div>
            </div>
          </>
        )}
        {/* Eyebrow chip — same on both cover styles */}
        <div style={{
          position: "absolute", top: 10, left: 10,
          fontSize: 9, fontWeight: 800, color: GOLD,
          background: "rgba(0,0,0,0.78)",
          border: `1px solid ${GOLD}66`,
          padding: "3px 8px", borderRadius: 4,
          letterSpacing: 1.2,
        }}>
          LESSON
        </div>
        {lesson.duration && (
          <div style={{
            position: "absolute", bottom: 10, right: 10,
            display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: 10, fontWeight: 700, color: "#fff",
            background: "rgba(0,0,0,0.7)", padding: "3px 7px",
            borderRadius: 4, fontFamily: "'JetBrains Mono', monospace",
          }}>
            <Clock size={9}/> {lesson.duration}
          </div>
        )}
      </div>

      <div style={{ padding: "12px 14px 14px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{
          fontSize: 14, fontWeight: 700, color: C.text, lineHeight: 1.3, marginBottom: 6,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {lesson.title}
        </div>
        {lesson.sub && (
          <div style={{
            fontSize: 12, color: C.textMid || C.textDim, lineHeight: 1.5,
            display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {lesson.sub}
          </div>
        )}
      </div>
    </button>
  );
}
